import { useMemo, useRef, useState } from "react";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { Input } from "@/components/ui/input";

type Section = {
  id: string;
  title: string;
  blurb: string;
  content: React.ReactNode;
};

export default function DocsPage() {
  const [query, setQuery] = useState("");
  const refs = useRef<Record<string, HTMLElement | null>>({});

  const sections = useMemo(() => getSections(), []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sections;
    return sections.filter(
      (s) =>
        s.title.toLowerCase().includes(q) ||
        s.blurb.toLowerCase().includes(q) ||
        sectionText(s.content).toLowerCase().includes(q),
    );
  }, [query, sections]);

  const jump = (id: string) => {
    const el = refs.current[id];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-heading text-3xl font-light sm:text-4xl">
          <span className="text-bitrefinery-red">D</span>ocs
        </h1>
        <p className="mt-2 max-w-3xl text-sm text-bitrefinery-text text-body">
          Operator's guide to the mail admin app. How each page is wired to Postfix /
          Dovecot / rspamd under the hood, the common workflows, and the pitfalls to watch
          for. For the on-disk install and rollback steps, see
          <code className="mx-1 rounded bg-slate-900 px-1 py-0.5 font-mono text-xs text-slate-100">
            ~/mail-admin-runbook.md
          </code>
          on the server.
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-[220px_1fr]">
        <aside className="space-y-3 md:sticky md:top-6 md:self-start">
          <div className="relative">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              className="pl-8"
              placeholder="Search docs…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <nav className="space-y-0.5 text-sm">
            {filtered.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => jump(s.id)}
                className="block w-full truncate rounded px-2 py-1 text-left text-muted-foreground hover:bg-accent hover:text-foreground"
              >
                {s.title}
              </button>
            ))}
            {filtered.length === 0 && (
              <p className="px-2 py-1 text-xs text-muted-foreground">
                No matches.
              </p>
            )}
          </nav>
        </aside>

        <div className="space-y-10">
          {filtered.map((s) => (
            <section
              key={s.id}
              id={s.id}
              ref={(el) => (refs.current[s.id] = el)}
              className="scroll-mt-6 rounded-2xl border border-border bg-card p-6 shadow-sm"
            >
              <h2 className="text-heading text-2xl font-light">{s.title}</h2>
              <p className="mt-1 text-sm text-muted-foreground">{s.blurb}</p>
              <div className="docs mt-4 text-sm leading-relaxed">{s.content}</div>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}

/* --- section authoring helpers ------------------------------------------ */

function Code({ children }: { children: React.ReactNode }) {
  return (
    <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs text-foreground">
      {children}
    </code>
  );
}

function Pre({ children }: { children: string }) {
  return (
    <pre className="mt-2 overflow-x-auto rounded-md bg-slate-900 p-3 font-mono text-xs leading-relaxed text-slate-100">
      {children}
    </pre>
  );
}

function H3({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="mt-4 text-sm font-medium uppercase tracking-wide text-muted-foreground">
      {children}
    </h3>
  );
}

function P({ children }: { children: React.ReactNode }) {
  return <p className="mt-2">{children}</p>;
}

function UL({ children }: { children: React.ReactNode }) {
  return <ul className="ml-5 mt-2 list-disc space-y-1">{children}</ul>;
}

function OL({ children }: { children: React.ReactNode }) {
  return <ol className="ml-5 mt-2 list-decimal space-y-1">{children}</ol>;
}

function Note({
  tone = "info",
  children,
}: {
  tone?: "info" | "warn" | "danger";
  children: React.ReactNode;
}) {
  const classes =
    tone === "danger"
      ? "border-rose-200 bg-rose-50 text-rose-900"
      : tone === "warn"
        ? "border-amber-200 bg-amber-50 text-amber-900"
        : "border-border bg-muted/50 text-foreground";
  return (
    <div className={`mt-3 rounded-md border p-3 text-xs ${classes}`}>{children}</div>
  );
}

/** Extract plain text for search-filter. */
function sectionText(node: React.ReactNode): string {
  if (node === null || node === undefined || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(sectionText).join(" ");
  if (typeof node === "object" && "props" in (node as any)) {
    return sectionText((node as any).props.children);
  }
  return "";
}

/* --- content ------------------------------------------------------------ */

function getSections(): Section[] {
  return [
    {
      id: "overview",
      title: "Overview",
      blurb:
        "How this app, Postfix, Dovecot, rspamd, and Postgres fit together.",
      content: (
        <>
          <P>
            This admin panel is a thin management layer over an otherwise stock self-hosted
            mail stack. Nothing here implements SMTP, IMAP, spam filtering, or DKIM signing
            directly — it only edits the backing data that those services read.
          </P>
          <H3>Flow of a single inbound message</H3>
          <OL>
            <li>
              <b>Postfix</b> on port 25 accepts the message. It looks up the recipient in
              three Postgres-backed maps — <Code>virtual_mailbox_domains</Code>,{" "}
              <Code>virtual_mailbox_maps</Code>, and <Code>virtual_alias_maps</Code> — all
              backed by the <Code>mailserver</Code> DB via <Code>proxy:pgsql:</Code>.
            </li>
            <li>
              If the address has an alias (including mailbox forwarding), Postfix expands
              to the target(s) and delivers to each.
            </li>
            <li>
              <b>rspamd</b> scores the message (milter on 127.0.0.1:11332), signs DKIM for
              outbound, and can reject/greylist incoming.
            </li>
            <li>
              <b>Dovecot LMTP</b> takes the final recipient, runs its Sieve script, and
              writes to the Maildir at <Code>/var/vmail/&lt;domain&gt;/&lt;local&gt;/</Code>
              as uid <Code>vmail</Code> (5000). Quota is tracked in-Maildir.
            </li>
          </OL>
          <H3>What this app touches</H3>
          <UL>
            <li>
              Writes to tables <Code>domain</Code>, <Code>mailbox</Code>,{" "}
              <Code>alias</Code>, <Code>audit</Code> in the <Code>mailserver</Code> DB.
            </li>
            <li>
              Shells out (via <a href="#privileged-helper">one narrow sudo binary</a>) to
              <Code>doveadm</Code> (quota, sieve), <Code>postqueue</Code>/
              <Code>postsuper</Code> (queue), and <Code>rspamadm</Code> (DKIM keygen).
            </li>
            <li>
              Resolves DNS from the app host to render the DNS health page — read-only,
              never writes to a registrar.
            </li>
          </UL>
          <Note>
            Flat-file configs (<Code>/etc/dovecot/users</Code>,{" "}
            <Code>/etc/postfix/vmailbox</Code>, <Code>/etc/postfix/virtual</Code>) are{" "}
            <b>not read anymore</b>. They exist only as <Code>.bak-&lt;date&gt;</Code>
            copies for rollback. Edits there silently do nothing.
          </Note>
        </>
      ),
    },

    {
      id: "dashboard",
      title: "Dashboard",
      blurb:
        "The landing page. Six tiles, each cached server-side so a loaded dashboard doesn't hammer the mail system.",
      content: (
        <>
          <H3>What each tile shows</H3>
          <UL>
            <li>
              <b>Counters</b>: active / disabled mailboxes, active aliases, active
              domains, audit events since local midnight. Click through to the full
              page for each.
            </li>
            <li>
              <b>Storage</b>: total bytes used vs total quota allocated, plus the top 5
              mailboxes by percent-of-quota. The per-mailbox probe is a parallel{" "}
              <Code>doveadm quota get</Code> fan-out, cached 60 s.
            </li>
            <li>
              <b>Queue</b>: depth by state (active / deferred / held) and the oldest
              deferred age. Cached 15 s; the card also auto-refetches every 30 s.
            </li>
            <li>
              <b>System health</b>: <Code>systemctl is-active</Code> for{" "}
              <Code>postfix</Code>, <Code>dovecot</Code>, <Code>rspamd</Code>,{" "}
              <Code>mail-admin</Code>; TLS cert expiry (
              <Code>/etc/letsencrypt/live/cloud.aigrammys.com/cert.pem</Code>
              , amber &lt;30 d, red &lt;14 d); rspamd <Code>/stat</Code> counters
              (scanned / ham / spam).
            </li>
            <li>
              <b>Recent activity</b>: last 10 audit rows, clickable through to the full
              audit log.
            </li>
          </UL>

          <H3>Caching</H3>
          <P>
            Caches live in the mail-admin process memory (see{" "}
            <Code>backend/src/lib/cache.ts</Code>). Restarting the service evicts them;
            no external dependency. TTLs: counters 30 s (client-side React Query only),
            storage 60 s, queue 15 s, services 30 s, cert 1 h, rspamd 30 s.
          </P>

          <H3>Troubleshooting</H3>
          <UL>
            <li>
              <b>Storage widget stuck on "Sampling doveadm…"</b>: the helper is failing;
              see <a href="#troubleshooting">Troubleshooting → helper_failed</a>.
            </li>
            <li>
              <b>"rspamd unreachable"</b>: the rspamd controller isn't listening on{" "}
              <Code>127.0.0.1:11334</Code>. Usually means rspamd is down — the Services
              row will also show red.
            </li>
            <li>
              <b>Cert widget says "helper_failed"</b>: the helper binary hasn't been
              updated with the <Code>cert-expiry</Code> subcommand. Re-run the install
              step for the helper from the runbook.
            </li>
          </UL>
        </>
      ),
    },

    {
      id: "mailboxes",
      title: "Mailboxes",
      blurb:
        "Create, disable, reset password, set quota, and configure forwarding for a virtual mail user.",
      content: (
        <>
          <H3>Creating a mailbox</H3>
          <OL>
            <li>Open the Mailboxes page and click <b>New mailbox</b>.</li>
            <li>
              Enter the address — the domain part must already exist in the Domains page.
              If it doesn't, add it there first with <b>New domain</b>.
            </li>
            <li>
              Initial password: min 10 chars. It's hashed to SHA512-CRYPT before insert
              (see <Code>backend/src/lib/crypt.ts</Code>).
            </li>
            <li>
              Quota (MB): 0 = unlimited. Applied immediately via the Dovecot userdb SQL
              query.
            </li>
          </OL>
          <P>
            The Maildir at <Code>/var/vmail/&lt;domain&gt;/&lt;local&gt;/</Code> is{" "}
            <i>not</i> pre-created — Dovecot LMTP creates it on the first inbound delivery.
          </P>

          <H3>Quota bar</H3>
          <P>
            Each row fetches <Code>/api/mailboxes/:addr/usage</Code> which calls{" "}
            <Code>doveadm quota get</Code>. Values cache for 15 seconds. The bar turns
            amber above 80% and red above 95%. Dovecot grants a 10% grace on storage (
            <Code>quota_grace = 10%</Code> in <Code>99-nextcloud.conf</Code>) before it
            starts rejecting new mail with <Code>552 5.2.2 Mailbox is full</Code>.
          </P>

          <H3>Forwarding</H3>
          <P>
            The forwarding dialog writes to two mailbox columns, <Code>forward_to</Code>
            and <Code>forward_keep_copy</Code>. The Postfix alias-maps query{" "}
            <Code>UNION</Code>s them in:
          </P>
          <UL>
            <li>
              <b>keep copy = on</b>: alias expands to{" "}
              <Code>alice@example.com, external@ext.com</Code>. Postfix's alias-expansion
              loop detector recognizes the self-reference and treats it as a terminal
              local delivery — so a copy lands in the Maildir <i>and</i> one goes out.
            </li>
            <li>
              <b>keep copy = off</b>: alias expands to just the external target. Because
              virtual_alias_maps takes precedence over virtual_mailbox_maps, the local
              Maildir is bypassed entirely.
            </li>
          </UL>
          <Note tone="warn">
            Don't set <Code>forward_to</Code> back to the mailbox's own address — the API
            rejects that, but if you somehow manage to, Postfix will bounce with an
            expansion-limit error.
          </Note>

          <H3>Password reset</H3>
          <P>
            Hashes a new password and updates <Code>mailbox.password</Code>. Existing IMAP
            sessions keep working until they re-authenticate; the user's next login uses
            the new hash. No email notification is sent — distribute out of band.
          </P>

          <H3>Disable vs delete</H3>
          <P>
            The trash-can icon is <b>soft-delete</b>: sets <Code>active = false</Code>.
            Inbound mail bounces (Postfix sees no matching mailbox), IMAP auth fails. The
            Maildir stays on disk for 30 days, then the{" "}
            <Code>mail-purge-inactive.timer</Code> systemd unit removes the directory.
            Re-enable at any time before then by clicking the power icon again.
          </P>
        </>
      ),
    },

    {
      id: "aliases",
      title: "Aliases",
      blurb:
        "Route one address to one or more others. Fan-out by listing an alias multiple times.",
      content: (
        <>
          <H3>Semantics</H3>
          <P>
            An alias row is a pair: <Code>(address, goto)</Code>. Multiple rows with the
            same <Code>address</Code> fan out to every <Code>goto</Code>. The backend
            aggregates them with <Code>string_agg(goto, ',')</Code> before handing to
            Postfix.
          </P>
          <H3>Alias vs mailbox forwarding</H3>
          <P>
            Both end up as <Code>virtual_alias_maps</Code> rules under the hood; the
            difference is whether the source is <b>an address you don't run a mailbox
            for</b> (alias — e.g. <Code>postmaster@</Code>, <Code>sales@</Code>) or <b>a
            real mailbox whose mail should also go elsewhere</b> (forwarding — managed on
            the Mailboxes page). If you have both, the alias rows and the forwarding row
            are UNIONed at lookup time.
          </P>
          <Note>
            Aliases are hard-deleted from the UI (not soft-deleted) because they have no
            on-disk state to preserve.
          </Note>
        </>
      ),
    },

    {
      id: "domains",
      title: "Domains",
      blurb:
        "Virtual domains served by Postfix. The domain list drives which addresses are accepted.",
      content: (
        <>
          <P>
            Each row shows a domain's active flag and how many mailboxes and aliases live
            under it. Postfix's <Code>virtual_mailbox_domains</Code> map reads{" "}
            <Code>domain</Code> rows where <Code>active = true</Code> straight out of
            Postgres — adding a row here is all that's needed for the MTA to start
            accepting mail for that domain.
          </P>
          <H3>Adding a new domain</H3>
          <OL>
            <li>
              Click <b>New domain</b>, enter the name (e.g. <Code>example.com</Code>), and
              save. The backend validates the shape, inserts into the{" "}
              <Code>domain</Code> table with <Code>active=true</Code>, and logs a{" "}
              <Code>domain.create</Code> audit event.
            </li>
            <li>
              No Postfix or Dovecot reload is needed — lookups are live against Postgres.
            </li>
            <li>
              Publish the DNS records below at the registrar (see table). MX, SPF, DMARC
              are needed for mail to flow; DKIM is added from the DKIM page.
            </li>
            <li>
              Generate a DKIM key for the new domain on the DKIM page and publish the TXT
              record.
            </li>
            <li>Verify everything with the DNS health page.</li>
          </OL>

          <H3>DNS records to publish</H3>
          <P>
            Substitute <Code>&lt;domain&gt;</Code> with the domain you added and{" "}
            <Code>&lt;Public IP of this email server&gt;</Code> with the server's current
            public IP. (The IP can change — always check before publishing.) The MX target
            can be this server's own hostname (shown below) or the shared{" "}
            <Code>mail.aigrammys.com</Code> if the A record already exists there.
          </P>
          <Pre>{`Type   Name                                 Value
A      mail.<domain>                        <Public IP of this email server>
MX     <domain>.                            mail.<domain>   (priority 10)
TXT    <domain>.                            v=spf1 mx ~all
TXT    _dmarc.<domain>                      v=DMARC1; p=none; rua=mailto:postmaster@<domain>
TXT    <selector>._domainkey.<domain>       (generated on the DKIM page)`}</Pre>
          <UL>
            <li>
              <b>A</b> — lets other mail servers resolve the MX target to this host's IP.
              Skip if you're pointing MX at an existing hostname such as{" "}
              <Code>mail.aigrammys.com</Code> whose A record is already published.
            </li>
            <li>
              <b>MX</b> — tells the world to deliver mail for{" "}
              <Code>&lt;domain&gt;</Code> to the named host. Priority 10 is the convention
              for a single primary MX.
            </li>
            <li>
              <b>SPF</b> — <Code>v=spf1 mx ~all</Code> means "the hosts listed in my MX
              are allowed senders; everything else soft-fails." Tighten to{" "}
              <Code>-all</Code> once you're sure nothing else sends as this domain.
            </li>
            <li>
              <b>DMARC</b> — start at <Code>p=none</Code> to monitor; move to{" "}
              <Code>quarantine</Code> or <Code>reject</Code> once DKIM + SPF are green for
              a week or two.
            </li>
            <li>
              <b>DKIM</b> — the selector and key blob come from the DKIM page; paste the
              TXT content exactly as shown.
            </li>
            <li>
              <b>PTR</b> (reverse DNS of the server's IP) is set at the network provider,
              not the registrar. Some ISPs reject mail from hosts with missing or
              mismatched PTRs.
            </li>
          </UL>
          <Note>
            The UI only supports creating domains right now — there's no delete or
            disable action. To retire a domain, flip{" "}
            <Code>active=false</Code> (or delete the row) via SQL after you've removed the
            mailboxes and aliases under it.
          </Note>
        </>
      ),
    },

    {
      id: "dns",
      title: "DNS health",
      blurb:
        "Live resolution of MX, SPF, DMARC, DKIM, and the MX target's PTR — one glance per domain.",
      content: (
        <>
          <H3>What each check means</H3>
          <UL>
            <li>
              <b>MX record</b>: at least one MX points somewhere. The first by priority
              becomes the hostname used for the PTR check.
            </li>
            <li>
              <b>SPF (TXT @)</b>: looks for <Code>v=spf1 …</Code> on the apex. Passes if
              present, warns if there's no terminal <Code>~all</Code> / <Code>-all</Code>{" "}
              (anyone can claim to be you).
            </li>
            <li>
              <b>DMARC (_dmarc TXT)</b>: looks for <Code>v=DMARC1; …</Code>. Warns on{" "}
              <Code>p=none</Code> — that's monitor-only; tighten to{" "}
              <Code>quarantine</Code> or <Code>reject</Code> once you're confident DKIM
              and SPF pass.
            </li>
            <li>
              <b>DKIM &lt;selector&gt;</b>: for every local DKIM key you've generated, we
              query <Code>&lt;selector&gt;._domainkey.&lt;domain&gt;</Code> and check for{" "}
              <Code>v=DKIM1;</Code> and a <Code>p=</Code> public-key blob.
            </li>
            <li>
              <b>PTR</b>: reverse DNS of the IP the MX resolves to. Warns if it doesn't
              match the MX hostname — some ISPs reject outbound mail from hosts with
              mismatched PTRs.
            </li>
          </UL>
          <H3>Common failures and fixes</H3>
          <UL>
            <li>
              <b>DKIM fails for a selector you just generated</b>: you haven't published
              the TXT record yet, or DNS hasn't propagated (usually ≤5 min at GoDaddy).
              Paste the record from the DKIM page and hit <b>Recheck</b>.
            </li>
            <li>
              <b>PTR warns</b>: the PTR for <Code>66.117.1.139</Code> must be set at the
              IP owner (network provider, not the registrar). The runbook notes this.
            </li>
            <li>
              <b>MX fails</b>: MX record missing or NS delegation hasn't finished. Check
              at <Code>dig MX domain.com</Code> from a different host.
            </li>
          </UL>
          <Note>
            Resolution goes through the mail-admin host's resolver. If the box itself is
            using a private nameserver with different answers from the public one,
            external mail servers won't see what this page sees.
          </Note>
        </>
      ),
    },

    {
      id: "dkim",
      title: "DKIM",
      blurb:
        "Generate, rotate, and delete DKIM signing keys. TXT records are shown for pasting into DNS.",
      content: (
        <>
          <H3>Generating a new selector</H3>
          <OL>
            <li>
              Click <b>New selector</b>, pick the domain, give it a selector name (common
              convention: the year, e.g. <Code>2026</Code>).
            </li>
            <li>
              The backend calls <Code>rspamadm dkim_keygen</Code> through the helper,
              writes the private key to{" "}
              <Code>/var/lib/rspamd/dkim/&lt;domain&gt;.&lt;selector&gt;.key</Code>{" "}
              (owned by <Code>_rspamd</Code>, mode 0440).
            </li>
            <li>
              The dialog shows the TXT record content. Copy it, paste into DNS at the
              name <Code>&lt;selector&gt;._domainkey.&lt;domain&gt;</Code>, wait a couple
              minutes for propagation.
            </li>
            <li>
              Confirm on the DNS health page that the DKIM check for that selector turns
              green.
            </li>
          </OL>

          <H3>Rotating to a new selector</H3>
          <P>
            rspamd's <Code>/etc/rspamd/local.d/dkim_signing.conf</Code> currently pins{" "}
            <b>one global selector</b> (<Code>selector = "2026"</Code>). Generating a new
            key does <i>not</i> automatically switch signing to it — the config still
            names the old selector.
          </P>
          <OL>
            <li>Generate the new key as above; publish its TXT record; wait for DNS.</li>
            <li>
              Edit <Code>/etc/rspamd/local.d/dkim_signing.conf</Code>, change the{" "}
              <Code>selector =</Code> line, and <Code>sudo systemctl reload rspamd</Code>.
            </li>
            <li>
              Leave the <b>old key + TXT record</b> in place for ~48h so messages already
              in flight signed under the old selector still verify.
            </li>
            <li>Only then delete the old selector (from the UI) and its DNS TXT record.</li>
          </OL>

          <Note tone="warn">
            Deleting a selector the rspamd config still names will break outbound signing
            — messages will go out unsigned and likely land in spam. Rotate config first,
            delete key second.
          </Note>
        </>
      ),
    },

    {
      id: "sieve",
      title: "Sieve",
      blurb:
        "Per-mailbox filter scripts (RFC 5228). The vacation preset generates an RFC 5230 autoresponder.",
      content: (
        <>
          <H3>How Sieve works here</H3>
          <P>
            Dovecot LMTP runs each inbound message through the user's{" "}
            <b>active</b> Sieve script before writing to disk. Inactive scripts are parked
            on disk but not consulted. The editor talks to Dovecot via{" "}
            <Code>doveadm sieve</Code>, which stores scripts under{" "}
            <Code>~/sieve/&lt;name&gt;.sieve</Code> with{" "}
            <Code>~/.dovecot.sieve</Code> symlinked to the active one.
          </P>

          <H3>Useful snippets</H3>
          <Pre>{`# File "[newsletter]"-tagged mail into a folder
require ["fileinto"];
if header :contains "Subject" "[newsletter]" {
  fileinto "Newsletters";
  stop;
}

# Flag mail from a specific sender
require ["imap4flags"];
if address :is "from" "boss@aigrammys.com" {
  addflag "\\\\Flagged";
}

# Reject an address entirely
require ["reject"];
if address :is "from" "spammy@bad.example" {
  reject "Mail from this address is not accepted.";
}`}</Pre>

          <H3>Vacation autoresponder</H3>
          <P>
            Click the <b>Vacation preset</b> button on the Sieve page. It writes a script
            named <Code>vacation</Code> and activates it. Dovecot's Pigeonhole vacation
            extension only replies <b>once per sender per reply-interval</b> (default 7
            days), so repeat senders won't get flooded.
          </P>
          <P>
            The generated script uses <Code>currentdate</Code> with <Code>:value "ge"</Code>/
            <Code>"le"</Code> comparisons to bracket the vacation window, so the
            autoresponder stops firing outside the start/end dates you set.
          </P>

          <Note>
            Only the <b>active</b> script runs. If a user has their own script active and
            you activate a new one from here, theirs gets deactivated (but not deleted).
          </Note>
        </>
      ),
    },

    {
      id: "queue",
      title: "Queue",
      blurb:
        "Inspect and act on the Postfix mail queue: flush, hold, release, delete.",
      content: (
        <>
          <H3>Why messages sit here</H3>
          <UL>
            <li>
              Remote server is down or slow — Postfix defers and retries on an exponential
              backoff.
            </li>
            <li>Greylisting at the remote — typically clears on the next attempt.</li>
            <li>DNS resolution failure for the recipient domain.</li>
            <li>Recipient TLS negotiation failed and you require TLS.</li>
            <li>Local: rspamd paused, disk full, or Dovecot LMTP socket missing.</li>
          </UL>

          <H3>Actions</H3>
          <UL>
            <li>
              <b>Flush (play icon)</b>: re-queue this one message for immediate delivery
              (<Code>postsuper -H</Code>). Use when you know the transient cause is gone.
            </li>
            <li>
              <b>Hold (hand icon)</b>: move to the held queue (<Code>postsuper -h</Code>).
              Postfix stops retrying until you release. Good for quarantining a
              suspected spam burst before deletion.
            </li>
            <li>
              <b>Delete (trash)</b>: <Code>postsuper -d</Code>. Message is dropped with no
              bounce. Sender sees their own bounce eventually (from their outbound server
              timing out).
            </li>
            <li>
              <b>Flush all</b>: top-right button. <Code>postqueue -f</Code> tries every
              deferred message immediately. Safe — just an eager retry.
            </li>
          </UL>

          <Note tone="warn">
            The queue refreshes every 15 seconds. If the list jumps between renders, it's
            just Postfix succeeding — you're watching real delivery happen.
          </Note>
        </>
      ),
    },

    {
      id: "audit",
      title: "Audit",
      blurb: "Every write is logged to the audit table with admin, IP, and details.",
      content: (
        <>
          <P>
            Anything that mutates state — creating a mailbox, resetting a password,
            generating a DKIM key, flushing the queue — calls <Code>logAudit()</Code> on
            its way out. The row includes the admin's email, the action name, the
            target (usually an address or selector), the admin's IP, and a JSON blob of
            action-specific details.
          </P>
          <H3>Filtering</H3>
          <UL>
            <li>
              <b>Admin</b>: filter by the email of the admin who made the change.
            </li>
            <li>
              <b>Action</b>: partial match on the dotted action name, e.g.{" "}
              <Code>mailbox.</Code> for all mailbox events, <Code>dkim.</Code> for DKIM.
            </li>
            <li>
              <b>Target</b>: partial match on the target string.
            </li>
          </UL>
          <H3>Retention</H3>
          <P>
            No automatic pruning. The table will grow indefinitely. At your current
            volume that's fine for years; if you want a limit, add a cron that deletes
            rows older than N days:
          </P>
          <Pre>{`sudo -u postgres psql -d mailserver \\
  -c "DELETE FROM audit WHERE at < now() - interval '2 years';"`}</Pre>
        </>
      ),
    },

    {
      id: "access",
      title: "Access control",
      blurb:
        "Email OTP login, session model, how to grant or revoke admin access.",
      content: (
        <>
          <H3>Who can log in</H3>
          <P>
            The allow-list is <Code>ALLOWED_ADMINS</Code> in{" "}
            <Code>/sites/mail-admin/backend/.env</Code> — a comma-separated lowercase
            list of email addresses. Changes require a service restart:
          </P>
          <Pre>{`sudoedit /sites/mail-admin/backend/.env
# edit the ALLOWED_ADMINS line
sudo systemctl restart mail-admin
sudo journalctl -u mail-admin -n 20 --no-pager    # verify clean start`}</Pre>
          <P>
            Requests for OTPs from addresses not on the list return <Code>{"{ ok: true }"}</Code>{" "}
            silently (no email sent). This is intentional — it prevents enumerating valid
            admin addresses.
          </P>

          <H3>OTP flow</H3>
          <UL>
            <li>6-digit numeric code, 10-minute TTL.</li>
            <li>Max 5 verify attempts per code.</li>
            <li>Max 5 OTP requests per email per hour (rate limit).</li>
            <li>Codes and attempt counters live in Redis, keyed by lowercased email.</li>
          </UL>

          <H3>Sessions</H3>
          <UL>
            <li>Server-side session in Redis, keyed by a 64-char random SID.</li>
            <li>
              Absolute lifetime 7 days. No sliding renewal — a session is valid until the
              TTL expires, then you re-OTP.
            </li>
            <li>
              Cookie is <Code>HttpOnly</Code>, <Code>Secure</Code>,{" "}
              <Code>SameSite=strict</Code>, served only from the admin domain.
            </li>
            <li>Logging out destroys the server-side session immediately.</li>
          </UL>
          <Note>
            No TOTP 2FA yet — single-factor via access to the admin's mail. If the mail
            account is compromised, the admin panel is compromised. This is on the list of
            future hardening steps.
          </Note>
        </>
      ),
    },

    {
      id: "privileged-helper",
      title: "Privileged helper",
      blurb:
        "The one sudo surface the backend uses. Anything that needs root goes through /usr/local/sbin/mail-admin-helper.",
      content: (
        <>
          <P>
            The <Code>mail-admin</Code> service runs as the unprivileged{" "}
            <Code>mailadmin</Code> user. For operations that need root —{" "}
            <Code>doveadm quota</Code>, <Code>doveadm sieve</Code>,{" "}
            <Code>postqueue</Code>, <Code>postsuper</Code>, <Code>rspamadm dkim_keygen</Code>{" "}
            — it shells out to <Code>/usr/local/sbin/mail-admin-helper</Code> via{" "}
            <Code>sudo -n</Code>.
          </P>

          <H3>Narrow sudo surface</H3>
          <Pre>{`# /etc/sudoers.d/mail-admin
mailadmin ALL=(root) NOPASSWD: /usr/local/sbin/mail-admin-helper
Defaults!/usr/local/sbin/mail-admin-helper !requiretty`}</Pre>
          <P>
            <b>Only that one binary.</b> Not <Code>doveadm</Code>. Not a shell. If the
            Node process is ever compromised, it can call the helper with arbitrary
            subcommand arguments — but the helper validates every argument against a
            regex (email / domain / selector / queue-id shapes) before shelling out, so
            you can't smuggle <Code>;rm -rf</Code> or unquoted globs.
          </P>

          <H3>Subcommands</H3>
          <UL>
            <li>
              <Code>quota-get &lt;addr&gt;</Code>
            </li>
            <li>
              <Code>sieve-list | sieve-get | sieve-put | sieve-activate | sieve-deactivate | sieve-delete</Code>
            </li>
            <li>
              <Code>dkim-list | dkim-generate | dkim-delete</Code>
            </li>
            <li>
              <Code>queue-list | queue-flush | queue-delete &lt;id&gt; | queue-hold &lt;id&gt; | queue-release &lt;id&gt;</Code>
            </li>
          </UL>

          <H3>Reviewing what the helper allows</H3>
          <Pre>{`# Inspect the binary and its validation regexes:
sudo less /usr/local/sbin/mail-admin-helper

# Sanity-check the sudoers file:
sudo visudo -cf /etc/sudoers.d/mail-admin`}</Pre>

          <Note tone="warn">
            If you ever edit the helper, run <Code>sudo visudo -cf /etc/sudoers.d/mail-admin</Code>{" "}
            again and try the failure cases (e.g. <Code>mail-admin-helper quota-get "a b"</Code>)
            to confirm the regex still rejects garbage arguments.
          </Note>
        </>
      ),
    },

    {
      id: "workflows",
      title: "Common workflows",
      blurb: "Step-by-step for the tasks you'll do most often.",
      content: (
        <>
          <H3>Onboard a new user</H3>
          <OL>
            <li>Mailboxes → New mailbox → fill in address, initial password, quota.</li>
            <li>Hand the credentials to the user (out of band).</li>
            <li>
              User configures their IMAP client against{" "}
              <Code>mail.&lt;domain&gt;:993</Code> (IMAPS) and{" "}
              <Code>mail.&lt;domain&gt;:587</Code> (submission, STARTTLS).
            </li>
          </OL>

          <H3>Turn on "forward to my phone, keep a copy"</H3>
          <OL>
            <li>Click the forwarding column on the user's Mailboxes row.</li>
            <li>Enter the external address.</li>
            <li>Leave "Keep a copy locally" on.</li>
            <li>Save. No reload needed — next inbound message will fan out.</li>
          </OL>

          <H3>Set up an out-of-office</H3>
          <OL>
            <li>Sieve page → pick the mailbox → <b>Vacation preset</b>.</li>
            <li>
              Set subject, body, reply interval (days). Optionally bracket with
              start/end dates.
            </li>
            <li>Save &amp; activate. The script name is <Code>vacation</Code>.</li>
            <li>
              When the user returns, come back and either delete the script or deactivate
              it. If you leave it active but outside the date window, it stops firing on
              its own.
            </li>
          </OL>

          <H3>Cut over a new domain to this server</H3>
          <OL>
            <li>Domains page → <b>New domain</b> → enter the name and save.</li>
            <li>Generate a DKIM key for it; publish the TXT.</li>
            <li>
              Publish SPF (<Code>v=spf1 mx ~all</Code>) and DMARC (
              <Code>v=DMARC1; p=none; rua=mailto:postmaster@&lt;domain&gt;</Code> to
              start) on the apex / <Code>_dmarc</Code>.
            </li>
            <li>Create initial mailboxes or aliases.</li>
            <li>Check the DNS health page — all should be pass/warn, none fail.</li>
            <li>
              Flip the MX at the registrar to <Code>mail.aigrammys.com</Code>.
            </li>
            <li>
              Send a test from a gmail account; watch for the delivery in the target
              Maildir, and check the Queue page if it doesn't appear within a minute.
            </li>
          </OL>

          <H3>Rotate a DKIM key (yearly)</H3>
          <P>
            See the <a href="#dkim">DKIM section</a> — the full rotation sequence with
            the rspamd config step is there.
          </P>
        </>
      ),
    },

    {
      id: "troubleshooting",
      title: "Troubleshooting",
      blurb:
        "Where to look when something isn't working, and the commands that answer the question fastest.",
      content: (
        <>
          <H3>A user can't log in to IMAP</H3>
          <OL>
            <li>
              Verify the mailbox exists and is active on the Mailboxes page (if the row
              has a grey "disabled" badge, enable it).
            </li>
            <li>
              From a shell on the server:
              <Pre>{`sudo doveadm auth test user@domain.com
# expected: passdb: <addr> auth succeeded
#           extra fields: user=<addr>
sudo doveadm user user@domain.com      # prints uid/gid/home/quota_rule`}</Pre>
            </li>
            <li>
              Tail logs:
              <Pre>{`sudo journalctl -u dovecot --since '5 min ago' | tail -40`}</Pre>
            </li>
          </OL>

          <H3>Mail-admin UI says "helper_failed"</H3>
          <UL>
            <li>
              Sudo is misconfigured or the helper is missing. Try manually as the service
              user:
              <Pre>{`sudo -u mailadmin sudo -n /usr/local/sbin/mail-admin-helper help`}</Pre>
            </li>
            <li>
              If that fails with "sudo: a password is required", the sudoers file isn't
              installed. Re-run step 1 of the install sequence in the runbook.
            </li>
            <li>
              If it fails with "doveadm: command not found" or similar inside the helper,
              the relevant package is missing:
              <Code>dovecot-sieve</Code>, <Code>dovecot-managesieved</Code>, or{" "}
              <Code>rspamd</Code> (which ships <Code>rspamadm</Code>).
            </li>
          </UL>

          <H3>Mail queue growing</H3>
          <OL>
            <li>Open the Queue page, read the rightmost "delay_reason" column.</li>
            <li>
              Per-message log:
              <Pre>{`sudo journalctl -u postfix | grep <queue-id>`}</Pre>
            </li>
            <li>
              If all are deferred with "connection refused" to a specific remote, that
              remote is down. Leave Postfix to retry.
            </li>
            <li>
              If many target your own Dovecot LMTP socket, Dovecot is down — restart
              Dovecot, then <b>Flush all</b>.
            </li>
          </OL>

          <H3>DKIM suddenly fails after I deleted a selector</H3>
          <P>
            You deleted the key rspamd's config still names. Edit{" "}
            <Code>/etc/rspamd/local.d/dkim_signing.conf</Code> to name a selector whose
            key still exists on disk, reload rspamd. Re-check the DKIM section for the
            correct rotation order.
          </P>

          <H3>Forwarding loop / bounce</H3>
          <P>
            If a mailbox forwards to an address that forwards back to it (directly or
            transitively), Postfix will hit{" "}
            <Code>virtual_alias_expansion_limit</Code> and bounce. Break the cycle at one
            end.
          </P>

          <H3>Where configs live</H3>
          <Pre>{`# Source of truth (edit here, then sudo cp into place):
ls ~/*.conf ~/mail-admin-*  ~/*-sql*.cf  ~/dovecot-*.conf

# Running configs:
ls /etc/nginx/sites-available/mail-admin \\
   /etc/postfix/main.cf /etc/postfix/sql/ \\
   /etc/dovecot/conf.d/99-nextcloud.conf /etc/dovecot/dovecot-sql.conf.ext \\
   /etc/systemd/system/mail-admin.service \\
   /etc/sudoers.d/mail-admin /usr/local/sbin/mail-admin-helper

# App source:
ls /sites/mail-admin/`}</Pre>
        </>
      ),
    },

    {
      id: "pointers",
      title: "Pointers & references",
      blurb: "Where to go next.",
      content: (
        <>
          <UL>
            <li>
              Roadmap & release notes: <Code>~/mail-admin-roadmap.md</Code> on the server.
            </li>
            <li>
              On-disk install + rollback sequence:{" "}
              <Code>~/mail-admin-runbook.md</Code> on the server.
            </li>
            <li>
              Original Nextcloud + mail install plan:{" "}
              <Code>~/.claude/plans/i-am-losing-my-glowing-lamport.md</Code>.
            </li>
            <li>
              Dovecot quota plugin docs:{" "}
              <a
                className="text-bitrefinery-red underline"
                href="https://doc.dovecot.org/configuration_manual/quota/"
                target="_blank"
                rel="noreferrer"
              >
                doc.dovecot.org/configuration_manual/quota
              </a>
              .
            </li>
            <li>
              Sieve language reference (RFC 5228):{" "}
              <a
                className="text-bitrefinery-red underline"
                href="https://datatracker.ietf.org/doc/html/rfc5228"
                target="_blank"
                rel="noreferrer"
              >
                rfc5228
              </a>
              . Vacation extension (RFC 5230):{" "}
              <a
                className="text-bitrefinery-red underline"
                href="https://datatracker.ietf.org/doc/html/rfc5230"
                target="_blank"
                rel="noreferrer"
              >
                rfc5230
              </a>
              .
            </li>
            <li>
              rspamd DKIM signing docs:{" "}
              <a
                className="text-bitrefinery-red underline"
                href="https://rspamd.com/doc/modules/dkim_signing.html"
                target="_blank"
                rel="noreferrer"
              >
                rspamd.com/doc/modules/dkim_signing
              </a>
              .
            </li>
            <li>
              Postfix postqueue / postsuper manpages:{" "}
              <a
                className="text-bitrefinery-red underline"
                href="https://www.postfix.org/postqueue.1.html"
                target="_blank"
                rel="noreferrer"
              >
                postqueue(1)
              </a>
              ,{" "}
              <a
                className="text-bitrefinery-red underline"
                href="https://www.postfix.org/postsuper.1.html"
                target="_blank"
                rel="noreferrer"
              >
                postsuper(1)
              </a>
              .
            </li>
          </UL>
        </>
      ),
    },
  ];
}
